import styled from "@emotion/styled";

import { themeGet } from "src/lib/emotion/themeGet.ts";
import type {
  DetailedHTMLProps,
  HTMLAttributes,
  PropsWithChildren,
} from "react";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";

type HeadingProps = PropsWithChildren<
  {
    h: 1 | 2 | 3 | 4 | 5 | 6;
    isLoading?: boolean;
  } & HTMLAttributes<HTMLHeadingElement>
>;

export const Heading = ({
  h,
  children,
  isLoading = false,
  ...rest
}: HeadingProps) => {
  return [
    <H1 isLoading={isLoading} {...rest}>
      {children}
    </H1>,
    <H2 isLoading={isLoading} {...rest}>
      {children}
    </H2>,
    <H3 isLoading={isLoading} {...rest}>
      {children}
    </H3>,
    <H4 isLoading={isLoading} {...rest}>
      {children}
    </H4>,
    <H5 isLoading={isLoading} {...rest}>
      {children}
    </H5>,
    <H6 isLoading={isLoading} {...rest}>
      {children}
    </H6>,
  ][h - 1];
};

export const H1 = ({
  isLoading = false,
  children,
  ...rest
}: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement> & {
  isLoading?: boolean;
}) => {
  return (
    <StyledH1 {...rest}>
      <Skeleton isLoading={isLoading}>{children}</Skeleton>
    </StyledH1>
  );
};

export const H2 = ({
  isLoading = false,
  children,
  ...rest
}: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement> & {
  isLoading?: boolean;
}) => {
  return (
    <StyledH2 {...rest}>
      <Skeleton isLoading={isLoading}>{children}</Skeleton>
    </StyledH2>
  );
};

export const H3 = ({
  isLoading = false,
  children,
  ...rest
}: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement> & {
  isLoading?: boolean;
}) => {
  return (
    <StyledH3 {...rest}>
      <Skeleton isLoading={isLoading}>{children}</Skeleton>
    </StyledH3>
  );
};

export const H4 = ({
  isLoading = false,
  children,
  ...rest
}: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement> & {
  isLoading?: boolean;
}) => {
  return (
    <StyledH4 {...rest}>
      <Skeleton isLoading={isLoading}>{children}</Skeleton>
    </StyledH4>
  );
};

export const H5 = ({
  isLoading = false,
  children,
  ...rest
}: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement> & {
  isLoading?: boolean;
}) => {
  return (
    <StyledH5 {...rest}>
      <Skeleton isLoading={isLoading}>{children}</Skeleton>
    </StyledH5>
  );
};

export const H6 = ({
  isLoading = false,
  children,
  ...rest
}: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement> & {
  isLoading?: boolean;
}) => {
  return (
    <StyledH6 {...rest}>
      <Skeleton isLoading={isLoading}>{children}</Skeleton>
    </StyledH6>
  );
};

const StyledH1 = styled.h1`
  margin: 0;
  font-size: ${themeGet("fontSizesV2.h1")};
  font-weight: ${themeGet("fontWeights.semibold")};
  line-height: 53px; // todo: convert to percents
`;

const StyledH2 = styled.h2`
  margin: 0;
  font-size: ${themeGet("fontSizesV2.h2")};
  font-weight: ${themeGet("fontWeights.semibold")};
  line-height: 44px; // todo: convert to percents
`;
const StyledH3 = styled.h3`
  margin: 0;
  font-size: ${themeGet("fontSizesV2.h3")};
  font-weight: ${themeGet("fontWeights.semibold")};
  line-height: 36px; // todo: convert to percents
`;
const StyledH4 = styled.h4`
  margin: 0;
  font-size: ${themeGet("fontSizesV2.h4")};
  font-weight: ${themeGet("fontWeights.semibold")};
  line-height: 30px; // todo: convert to percents
`;
const StyledH5 = styled.h5`
  margin: 0;
  font-size: ${themeGet("fontSizesV2.h5")};
  font-weight: ${themeGet("fontWeights.semibold")};
  line-height: 26px; // todo: convert to percents
`;
const StyledH6 = styled.h6`
  margin: 0;
  font-size: ${themeGet("fontSizesV2.h6")};
  font-weight: ${themeGet("fontWeights.semibold")};
  line-height: 21px; // todo: convert to percents
`;
